import React, { useState, useEffect } from "react";
import "../style.scss";
import { Link } from "react-router-dom";
import { httpService } from "../../../base/services/httpService.service";
import { useDispatch } from "react-redux";
import { LocalStorageService } from "../../../base/services/local-storage.service";
import { Formik, Form, Field } from "formik";
import { useNavigate } from "react-router-dom";
import { registerUserOrg } from "../authSlice";
import * as Yup from "yup";
import { DatePicker, Input, Select, Spin } from "antd";
import TermsConditionsModal from "../../../components/TermsConditionsModal";
import Toast from "../../../base/components/Toast";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "../../../i18n/LanguageContext";
import SignupLayout, {
   NO_AUTOFILL,
   ArrowIcon,
   SignupStepper,
   validateSignupStep,
   wizardEnterHandler
} from "../components/SignupLayout";

const initialValues = {
   email: "",
   password: "",
   confirmPassword: "",
   acceptTerms: false,
   internationalName: "",
   description: "",
   dateOfEstablishment: null,
   organizationSizeId: null,
   sectors: [],
   organizationEmail: "",
   baseCountryId: null,
   cityId: "",
   address: "",
   zipCode: ""
};

// Fields validated before "Continue" moves on from each step.
const STEP_FIELDS = [
   ["email", "password", "confirmPassword", "acceptTerms"],
   ["internationalName", "description", "dateOfEstablishment", "organizationSizeId", "sectors"],
   ["organizationEmail", "baseCountryId", "cityId", "address", "zipCode"]
];

const filterByLabel = (input, option) =>
   option?.label?.toLowerCase().includes(input.toLowerCase());

function RegistrationOfNationalOrganization() {
   const { t } = useTranslation();
   const { executeRecaptcha } = useGoogleReCaptcha();
   const [step, setStep] = useState(0);
   const [sectorList, setSectorList] = useState([]);
   const [orgSizeList, setOrgSizeList] = useState([]);
   const [countryList, setCountryList] = useState([]);
   const [cityList, setCityList] = useState([]);
   const [selectedCity, setSelectedCity] = useState(null);
   const [modalVisible, setModalVisible] = useState(false);
   const loading = useSelector((state) => state.auth.isLoading);
   const dispatch = useDispatch();
   const navigate = useNavigate();

   const steps = [
      t("signup-step-account"),
      t("profile-organization-label"),
      t("signup-step-contact")
   ];

   const validationSchema = Yup.object().shape({
      email: Yup.string()
         .email(t("invalid-email-address-validation"))
         .required(t("email-required-validation")),
      password: Yup.string()
         .min(8, t("password-min-length-validation"))
         .matches(
            /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*\d).*$/,
            t("password-complexity-validation")
         )
         .required(t("password-required-validation")),
      confirmPassword: Yup.string()
         .oneOf([Yup.ref("password"), null], t("passwords-must-match-validation"))
         .required(t("confirm-password-required-validation")),
      internationalName: Yup.string().required(t("organization-name-required-validation")),
      dateOfEstablishment: Yup.date()
         .nullable()
         .required(t("foundation-year-required-validation")),
      sectors: Yup.array()
         .min(1, t("sectors-min-validation"))
         .required(t("sectors-required-validation")),
      baseCountryId: Yup.string()
         .nullable()
         .required(t("base-country-required-validation")),
      cityId: Yup.string().nullable(),
      organizationSizeId: Yup.string()
         .nullable()
         .required(t("organization-size-required-validation")),
      address: Yup.string().required(t("organization-address-required-validation")),
      organizationEmail: Yup.string()
         .email(t("invalid-organization-email-validation"))
         .required(t("organization-email-required-validation")),
      acceptTerms: Yup.boolean().oneOf([true], t("accept-terms-required-validation"))
   });

   const onSubmit = async (values, { setFieldError }) => {
      if (!executeRecaptcha) {
         return;
      }

      if (cityList.length > 0 && selectedCity === "" && values.cityId === "") {
         setFieldError("cityId", t("city-required-validation"));
      }

      const recaptchaToken = await executeRecaptcha("login");
      try {
         const updatedValues = {
            ...values,
            dateOfEstablishment: values?.dateOfEstablishment.format("YYYY-MM-DD")
         };
         dispatch(
            registerUserOrg(
               updatedValues,
               (user) => {
                  LocalStorageService.setItem("userId", user.userId);
                  navigate(
                     `/auth/national-org-registration/verification?email=${encodeURIComponent(
                        updatedValues.email
                     )}`
                  );
               },
               recaptchaToken
            )
         );
      } catch (error) {
         if (error.response || error.response.data || error.response.data.errors) {
            const errorMessage = error.response.data.errors.message;
            Toast.error(errorMessage);
         }
      }
   };

   const goToNextStep = async (formikProps) => {
      if (await validateSignupStep(formikProps, STEP_FIELDS[step])) {
         setStep(step + 1);
         window.scrollTo(0, 0);
      }
   };

   const openTermsModal = () => {
      setModalVisible(!modalVisible);
   };

   const handleCountryChange = async (selectedOption) => {
      await httpService.get(`/countries/${selectedOption}/cities`, (res) => {
         const cityData = res.data.map((city) => ({
            value: city.id,
            label: city.name
         }));

         setCityList(cityData);
         setSelectedCity(null);
      });
   };

   useEffect(() => {
      httpService.get(`/Organizations/sectors`, (data) => {
         const sectorData = data.data.map((sector) => {
            return {
               value: sector.id,
               label: sector.name
            };
         });
         setSectorList(sectorData);
      });
      httpService.get(`/Organizations/sizes`, (data) => {
         const orgSizeData = data.data.map((size) => {
            return {
               value: size.id,
               label: size.range
            };
         });
         setOrgSizeList(orgSizeData);
      });
      httpService.get(`/countries`, (data) => {
         const countryData = data.data.map((country) => {
            return {
               value: country.id,
               label: country.name
            };
         });
         setCountryList(countryData);
      });
      httpService.get(`/Countries/cities`, (res) => {
         const cityData = res.data.map((city) => {
            return {
               value: city.id,
               label: city.name
            };
         });
         setCityList(cityData);
      });
   }, []);

   const fieldError = (errors, touched, name) =>
      errors[name] && touched[name] && <div className="error-message">{errors[name]}</div>;

   const optionalTag = <span className="kap-org-signup__optional">{t("description-optional-label")}</span>;

   return (
      <SignupLayout
         showcase="organization"
         {...(step === 0 ? { backTo: "/auth/registration" } : { onBack: () => setStep(step - 1) })}
      >
         <p className="kap-org-signup__eyebrow">{t("signup-create-account-as-an")}</p>
         <h1 className="kap-org-signup__title">{t("profile-organization-label")}</h1>

         <SignupStepper steps={steps} current={step} />

         <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
         >
            {(formikProps) => {
               const { values, setFieldValue, errors, touched, handleChange, handleBlur } =
                  formikProps;
               return (
                  <Form
                     className="kap-login__form"
                     style={{ maxWidth: "none" }}
                     onKeyDown={wizardEnterHandler(step === STEP_FIELDS.length - 1, () =>
                        goToNextStep(formikProps)
                     )}
                  >
                     {step === 0 && (
                        <>
                           <p className="kap-org-signup__section">{t("login-details-title")}</p>
                           <div className="kap-login__fields">
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">{t("email")}</label>
                                 <Field
                                    placeholder={t("email-placeholder")}
                                    className="form-control"
                                    type="email"
                                    name="email"
                                    autoComplete="off"
                                 />
                                 {fieldError(errors, touched, "email")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">{t("password-label")}</label>
                                 <Input.Password
                                    placeholder={t("password-label")}
                                    name="password"
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    maxLength={16}
                                 />
                                 {fieldError(errors, touched, "password")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("confirm-password-label")}
                                 </label>
                                 <Input.Password
                                    placeholder={t("password-label")}
                                    name="confirmPassword"
                                    value={values.confirmPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    maxLength={16}
                                 />
                                 {fieldError(errors, touched, "confirmPassword")}
                              </div>
                           </div>
                           <div className="kap-org-signup__terms">
                              <label className="d-flex align-items-start m-0">
                                 <Field type="checkbox" name="acceptTerms" />
                                 <span>
                                    {t("terms-agreement-prefix-text")}{" "}
                                    <Link onClick={openTermsModal} to="#">
                                       {t("terms-of-service-privacy-policy-link")}
                                    </Link>{" "}
                                    {t("and-conjunction-text")}{" "}
                                    <Link onClick={openTermsModal} to="#">
                                       {t("cookie-policy-link")}
                                    </Link>
                                 </span>
                              </label>
                              {fieldError(errors, touched, "acceptTerms")}
                           </div>
                        </>
                     )}

                     {step === 1 && (
                        <>
                           <p className="kap-org-signup__section">
                              {t("organization-details-tab")}
                           </p>
                           <div className="kap-login__fields">
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("organization-name-label")}
                                 </label>
                                 <Field
                                    placeholder={t("enter-organization-name-placeholder")}
                                    type="text"
                                    name="internationalName"
                                    className="form-control"
                                    autoComplete="off"
                                 />
                                 {fieldError(errors, touched, "internationalName")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label">
                                    {t("organization-description-label")} {optionalTag}
                                 </label>
                                 <Field
                                    placeholder={t("type-description-placeholder")}
                                    as="textarea"
                                    name="description"
                                    className="form-control kap-org-signup__textarea"
                                    maxLength={100}
                                    autoComplete="off"
                                 />
                                 <span className="kap-org-signup__counter">
                                    {values?.description?.length}/100
                                 </span>
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("foundation-year-label")}
                                 </label>
                                 <div className="kap-org-signup__narrow">
                                    <DatePicker
                                       name="dateOfEstablishment"
                                       placeholder={t("select-date-placeholder")}
                                       onChange={(date) => setFieldValue("dateOfEstablishment", date)}
                                       disabledDate={(current) =>
                                          current && current > dayjs().endOf("day")
                                       }
                                       className="form-control"
                                       value={
                                          values?.dateOfEstablishment &&
                                          dayjs(values?.dateOfEstablishment)
                                       }
                                    />
                                 </div>
                                 {fieldError(errors, touched, "dateOfEstablishment")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("organization-size-label")}
                                 </label>
                                 <Select
                                    placeholder={t("select-organization-size-placeholder")}
                                    options={orgSizeList}
                                    value={values.organizationSizeId ?? undefined}
                                    onChange={(selectedOrgSize) =>
                                       setFieldValue("organizationSizeId", selectedOrgSize)
                                    }
                                    style={{ width: "100%" }}
                                 />
                                 {fieldError(errors, touched, "organizationSizeId")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("working-sectors-label")}
                                 </label>
                                 <Select
                                    mode="multiple"
                                    value={values.sectors}
                                    onChange={(selectedOptions) =>
                                       setFieldValue("sectors", selectedOptions)
                                    }
                                    showSearch
                                    autoComplete={NO_AUTOFILL}
                                    filterOption={filterByLabel}
                                    options={sectorList}
                                    placeholder={t("select-sectors-placeholder")}
                                    style={{ width: "100%" }}
                                 />
                                 {fieldError(errors, touched, "sectors")}
                              </div>
                           </div>
                        </>
                     )}

                     {step === 2 && (
                        <>
                           <p className="kap-org-signup__section">
                              {t("contact-information-tab")}
                           </p>
                           <div className="kap-login__fields">
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("organizations-email-label")}
                                 </label>
                                 <Field
                                    placeholder={t("enter-organization-email-placeholder")}
                                    type="email"
                                    name="organizationEmail"
                                    className="form-control"
                                    autoComplete="off"
                                 />
                                 {fieldError(errors, touched, "organizationEmail")}
                              </div>
                              <div className="kap-org-signup__row">
                                 <div className="form-group">
                                    <label className="kap-login__label">{t("city-label")}</label>
                                    <Select
                                       value={selectedCity ?? undefined}
                                       disabled={cityList.length === 0}
                                       options={cityList}
                                       onChange={(selectedOption) => {
                                          setSelectedCity(selectedOption);
                                          setFieldValue("cityId", selectedOption);
                                       }}
                                       showSearch
                                       autoComplete={NO_AUTOFILL}
                                       filterOption={filterByLabel}
                                       placeholder={t("select-base-city-placeholder")}
                                       style={{ width: "100%" }}
                                    />
                                    {fieldError(errors, touched, "cityId")}
                                 </div>
                                 <div className="form-group">
                                    <label className="kap-login__label kap-login__label--required">
                                       {t("base-country-label")}
                                    </label>
                                    <Select
                                       showSearch
                                       autoComplete={NO_AUTOFILL}
                                       filterOption={filterByLabel}
                                       placeholder={t("select-base-country-placeholder")}
                                       value={values.baseCountryId ?? undefined}
                                       options={countryList}
                                       onChange={(selectedOption) => {
                                          setSelectedCity(null);
                                          setFieldValue("baseCountryId", selectedOption);
                                          setFieldValue("cityId", null);
                                          handleCountryChange(selectedOption);
                                       }}
                                       style={{ width: "100%" }}
                                    />
                                    {fieldError(errors, touched, "baseCountryId")}
                                 </div>
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">{t("address-label")}</label>
                                 <Field
                                    placeholder={t("enter-organization-address-placeholder")}
                                    type="text"
                                    name="address"
                                    className="form-control"
                                    autoComplete="off"
                                 />
                                 {fieldError(errors, touched, "address")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label">
                                    {t("zip-code-label")} {optionalTag}
                                 </label>
                                 <Field
                                    placeholder={t("enter-organization-zip-code-placeholder")}
                                    type="text"
                                    name="zipCode"
                                    className="form-control"
                                    autoComplete="off"
                                 />
                              </div>
                           </div>
                        </>
                     )}

                     {/* Distinct keys so React swaps the element instead of flipping one
                         button to type="submit" mid-click, which would submit the form. */}
                     {step < STEP_FIELDS.length - 1 ? (
                        <button
                           key="continue"
                           type="button"
                           className="btn sign-btn font-weight-bold kap-login__submit kap-org-signup__submit"
                           onClick={() => goToNextStep(formikProps)}
                        >
                           {t("continue-button")}
                           <ArrowIcon />
                        </button>
                     ) : (
                        <button
                           key="submit"
                           type="submit"
                           className="btn sign-btn font-weight-bold kap-login__submit kap-org-signup__submit"
                           disabled={loading}
                        >
                           {loading ? <Spin /> : t("sign-up-link")}
                        </button>
                     )}
                  </Form>
               );
            }}
         </Formik>
         <TermsConditionsModal visible={modalVisible} onCancel={openTermsModal} />
      </SignupLayout>
   );
}
export default RegistrationOfNationalOrganization;
